import React, { useEffect, useState } from 'react';
import { adminApi, formatApiError } from './adminApi';
import {
  Mail,
  Send,
  AlertCircle,
  CheckCircle2,
  Users,
  Clock,
  Info,
  ChevronDown,
  ChevronUp,
  Globe,
  Lock,
  ExternalLink,
} from 'lucide-react';

const AdminBroadcast = () => {
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [sending, setSending] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [logsCache, setLogsCache] = useState({});
  const [publishingId, setPublishingId] = useState(null);

  const handleTogglePublic = async (id, current) => {
    setPublishingId(id);
    try {
      await adminApi.setBroadcastPublic(id, !current);
      setHistory((prev) =>
        prev.map((h) => (h.id === id ? { ...h, is_public: !current } : h))
      );
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setPublishingId(null);
    }
  };

  const refresh = async () => {
    const [s, h] = await Promise.all([
      adminApi.broadcastStatus(),
      adminApi.broadcastHistory(),
    ]);
    setStatus(s);
    setHistory(h);
  };

  useEffect(() => {
    refresh();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!subject.trim() || !body.trim()) {
      setError('Subject and body are required.');
      return;
    }
    setSending(true);
    try {
      // Auto-generate an idempotency key per click so a double-submit (e.g. accidentally
      // clicking "Send" twice) does not produce a duplicate broadcast on the server.
      const idem = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      const res = await adminApi.broadcastSend({ subject, body }, idem);
      if (res.warning) {
        setError(res.warning);
        setSuccess(res.message || '');
      } else {
        setSuccess(res.message || 'Broadcast queued.');
      }
      setSubject('');
      setBody('');
      refresh();
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setSending(false);
    }
  };

  const isDraftMode = status && status.configured === false;

  return (
    <div className="px-8 lg:px-12 py-10 max-w-5xl" data-testid="admin-broadcast-page">
      <header className="mb-8">
        <p className="text-[#83BF42] text-xs uppercase tracking-[0.2em] font-['Inter'] mb-2">Audience</p>
        <h1 className="text-white text-4xl font-light font-['Inter']">Newsletter Broadcast</h1>
        <p className="text-white/40 text-sm font-['Lato'] mt-2">
          Send updates to every newsletter subscriber.
        </p>
      </header>

      {/* Status banner */}
      {status && (
        <div
          data-testid="broadcast-status-banner"
          className={`flex items-start gap-3 px-5 py-4 rounded-xl mb-6 border ${
            isDraftMode
              ? 'bg-amber-500/5 border-amber-500/20 text-amber-200'
              : 'bg-[#83BF42]/5 border-[#83BF42]/20 text-[#83BF42]'
          }`}
        >
          <Info className="w-4 h-4 mt-0.5 shrink-0" />
          <div className="text-sm font-['Lato'] flex-1">
            {isDraftMode ? (
              <>
                <strong className="text-amber-300 font-['Inter'] mr-1">Draft mode.</strong>
                Messages save to history but won't be sent until <code className="bg-black/30 px-1.5 py-0.5 rounded text-xs">RESEND_API_KEY</code> is configured in the backend.
              </>
            ) : (
              <>
                <strong className="font-['Inter'] mr-1">Live.</strong>
                Sending via Resend from <code className="bg-black/30 px-1.5 py-0.5 rounded text-xs">{status.from_address || '(no sender)'}</code>.
              </>
            )}
          </div>
          {!isDraftMode && (
            <button
              onClick={async () => {
                setError(''); setSuccess('');
                try {
                  const r = await adminApi.broadcastTest();
                  setSuccess(`Test email sent (id: ${r.email_id?.slice(0, 8)}…). Check your inbox.`);
                } catch (err) {
                  setError(formatApiError(err?.response?.data?.detail) || err.message);
                }
              }}
              data-testid="broadcast-test-button"
              className="shrink-0 px-3 py-1.5 rounded-full text-xs font-['Inter'] border border-[#83BF42]/30 text-[#83BF42] hover:bg-[#83BF42]/10 transition"
            >
              Send test email
            </button>
          )}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-[#15151A] border border-white/5 rounded-2xl p-5">
          <div className="w-9 h-9 rounded-lg bg-[#83BF42]/10 text-[#83BF42] flex items-center justify-center mb-4">
            <Users className="w-4 h-4" />
          </div>
          <p className="text-white text-2xl font-light font-['Inter']">{status?.subscriber_count ?? 0}</p>
          <p className="text-white/40 text-xs uppercase tracking-wider font-['Inter'] mt-1">Subscribers</p>
        </div>
        <div className="bg-[#15151A] border border-white/5 rounded-2xl p-5">
          <div className="w-9 h-9 rounded-lg bg-blue-500/10 text-blue-400 flex items-center justify-center mb-4">
            <Mail className="w-4 h-4" />
          </div>
          <p className="text-white text-2xl font-light font-['Inter']">{history.length}</p>
          <p className="text-white/40 text-xs uppercase tracking-wider font-['Inter'] mt-1">Total broadcasts</p>
        </div>
        <div className="bg-[#15151A] border border-white/5 rounded-2xl p-5">
          <div className="w-9 h-9 rounded-lg bg-purple-500/10 text-purple-400 flex items-center justify-center mb-4">
            <Clock className="w-4 h-4" />
          </div>
          <p className="text-white text-base font-['Inter']">
            {history[0]?.created_at
              ? new Date(history[0].created_at).toLocaleDateString()
              : '—'}
          </p>
          <p className="text-white/40 text-xs uppercase tracking-wider font-['Inter'] mt-1">Last sent</p>
        </div>
      </div>

      {/* Compose */}
      <div className="bg-[#15151A] border border-white/5 rounded-2xl p-6 lg:p-8 mb-8">
        <h2 className="text-white text-base font-medium font-['Inter'] mb-5 flex items-center gap-2">
          <Send className="w-4 h-4 text-[#83BF42]" /> New broadcast
        </h2>

        {error && (
          <div data-testid="broadcast-error" className="flex items-start gap-2 mb-4 px-3 py-2.5 rounded-lg bg-red-500/10 border border-red-500/20 text-red-300 text-xs">
            <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {error}
          </div>
        )}
        {success && (
          <div data-testid="broadcast-success" className="flex items-start gap-2 mb-4 px-3 py-2.5 rounded-lg bg-[#83BF42]/10 border border-[#83BF42]/20 text-[#83BF42] text-xs">
            <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {success}
          </div>
        )}

        <form onSubmit={handleSubmit} data-testid="broadcast-form" className="space-y-4">
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-white/50 mb-1.5">Subject</label>
            <input
              required
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              data-testid="broadcast-subject"
              className="w-full px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-[#83BF42] transition-colors"
              placeholder="Insights — March issue"
            />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-white/50 mb-1.5">Body</label>
            <textarea
              required
              rows={8}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              data-testid="broadcast-body"
              className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white font-['Lato'] text-[15px] leading-relaxed focus:outline-none focus:border-[#83BF42] transition-colors resize-y"
              placeholder="Hi there, this month we covered..."
            />
            <p className="text-white/30 text-xs font-['Lato'] mt-1.5">
              Plain text. Line breaks preserved. Formatting will be applied by the email template.
            </p>
          </div>

          <button
            type="submit"
            disabled={sending}
            data-testid="broadcast-submit"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#83BF42] text-[#0F0F11] text-sm font-medium font-['Inter'] hover:bg-[#A8D96C] transition disabled:opacity-50"
          >
            <Send className="w-4 h-4" />
            {sending ? 'Working…' : isDraftMode ? 'Save draft' : `Send to ${status?.subscriber_count ?? 0}`}
          </button>
        </form>
      </div>

      {/* History */}
      <div className="bg-[#15151A] border border-white/5 rounded-2xl p-6 lg:p-8">
        <h2 className="text-white text-base font-medium font-['Inter'] mb-5">History</h2>
        {history.length === 0 ? (
          <p className="text-white/40 text-sm font-['Lato']" data-testid="broadcast-history-empty">
            No broadcasts yet.
          </p>
        ) : (
          <ul className="divide-y divide-white/5">
            {history.map((h) => {
              const isExpanded = expandedId === h.id;
              const logs = logsCache[h.id];
              const canExpand = h.status === 'sent' || h.status === 'partial' || h.status === 'failed';
              return (
              <li key={h.id} className="py-4" data-testid={`broadcast-history-${h.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <button
                    onClick={async () => {
                      if (!canExpand) return;
                      const next = isExpanded ? null : h.id;
                      setExpandedId(next);
                      if (next && !logsCache[h.id]) {
                        const data = await adminApi.broadcastLogs(h.id);
                        setLogsCache((c) => ({ ...c, [h.id]: data }));
                      }
                    }}
                    className="text-left min-w-0 flex-1"
                    data-testid={`broadcast-expand-${h.id}`}
                  >
                    <p className="text-white text-sm font-medium font-['Inter'] flex items-center gap-2">
                      {h.subject}
                      {canExpand && (isExpanded
                        ? <ChevronUp className="w-3.5 h-3.5 text-white/40" />
                        : <ChevronDown className="w-3.5 h-3.5 text-white/40" />)}
                    </p>
                    <p className="text-white/50 text-xs font-['Lato'] mt-1 line-clamp-2">{h.body}</p>
                    <p className="text-white/30 text-xs font-['Lato'] mt-2">
                      {h.status === 'sent' || h.status === 'partial'
                        ? `${h.sent_count}/${h.recipient_count} delivered${h.failed_count ? ` (${h.failed_count} failed)` : ''}`
                        : `${h.recipient_count} recipients`}
                      {' · '}
                      {h.created_at ? new Date(h.created_at).toLocaleString() : '—'}
                    </p>
                  </button>
                  <span
                    className={`shrink-0 inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-['Inter'] border ${
                      h.status === 'sent'
                        ? 'bg-[#83BF42]/15 text-[#83BF42] border-[#83BF42]/30'
                        : h.status === 'partial'
                        ? 'bg-amber-500/15 text-amber-300 border-amber-500/30'
                        : h.status === 'failed'
                        ? 'bg-red-500/15 text-red-300 border-red-500/30'
                        : h.status === 'sending'
                        ? 'bg-blue-500/15 text-blue-300 border-blue-500/30'
                        : 'bg-white/5 text-white/60 border-white/10'
                    }`}
                  >
                    {h.status === 'draft_no_provider' ? 'Draft' : h.status}
                  </span>
                </div>

                {/* Publish-to-archive toggle (only for delivered broadcasts) */}
                {(h.status === 'sent' || h.status === 'partial') && (
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-xs font-['Inter']">
                    <button
                      type="button"
                      onClick={() => handleTogglePublic(h.id, !!h.is_public)}
                      disabled={publishingId === h.id}
                      data-testid={`broadcast-toggle-public-${h.id}`}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border transition-colors disabled:opacity-50 ${
                        h.is_public
                          ? 'bg-[#83BF42]/15 text-[#83BF42] border-[#83BF42]/30 hover:bg-[#83BF42]/25'
                          : 'bg-white/5 text-white/60 border-white/10 hover:bg-white/10 hover:text-white'
                      }`}
                    >
                      {h.is_public ? <Globe className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                      {publishingId === h.id ? 'Updating…' : h.is_public ? 'Public archive' : 'Publish to archive'}
                    </button>
                    {h.is_public && (
                      <a
                        href={`/newsletter/${h.id}`}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`broadcast-archive-link-${h.id}`}
                        className="inline-flex items-center gap-1 text-white/40 hover:text-[#83BF42] transition-colors"
                      >
                        View public link <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                )}

                {isExpanded && (
                  <div className="mt-3 ml-2 pl-4 border-l border-white/10" data-testid={`broadcast-logs-${h.id}`}>
                    {!logs ? (
                      <p className="text-white/30 text-xs font-['Lato'] py-2">Loading recipient logs…</p>
                    ) : logs.length === 0 ? (
                      <p className="text-white/30 text-xs font-['Lato'] py-2">
                        No per-recipient logs (broadcast pre-dates instrumentation).
                      </p>
                    ) : (
                      <ul className="space-y-1.5 mt-2">
                        {logs.map((row) => (
                          <li
                            key={row.id}
                            className="flex items-center justify-between gap-3 text-xs font-['Lato']"
                            data-testid={`broadcast-log-row-${row.id}`}
                          >
                            <span className="text-white/70 truncate">{row.email}</span>
                            <span
                              className={`text-[10px] uppercase tracking-wider font-['Inter'] ${
                                row.status === 'sent' ? 'text-[#83BF42]' : 'text-red-300'
                              }`}
                            >
                              {row.status}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default AdminBroadcast;
